import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterOutlet, RouterLink, RouterLinkActive, NavigationEnd } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService, Role } from './core/auth/auth.service';
import { CompanySwitcherComponent } from './core/layout/company-switcher';
import { CreateMenuComponent } from './core/layout/create-menu';
import { GlobalSearchComponent } from './core/layout/global-search';

interface NavItem { path: string; label: string; icon: string; minRole?: Role; }
interface NavGroup { title: string; items: NavItem[]; minRole?: Role; }

@Component({
  selector: 'app-root',
  imports: [CommonModule, RouterOutlet, RouterLink, RouterLinkActive, CompanySwitcherComponent, CreateMenuComponent, GlobalSearchComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App {
  private router = inject(Router);
  private auth = inject(AuthService);

  private readonly allNavGroups: NavGroup[] = [
    {
      title: 'Overview',
      items: [{ path: '/dashboard', label: 'Dashboard', icon: 'dashboard' }]
    },
    {
      title: 'Sales',
      items: [
        { path: '/customers',         label: 'Customers',         icon: 'users' },
        { path: '/products',          label: 'Products & Services', icon: 'box' },
        { path: '/invoices',          label: 'Invoices',          icon: 'invoice' },
        { path: '/credits/customer',  label: 'Credit Notes',      icon: 'refund' },
        { path: '/payments/received', label: 'Received Payments', icon: 'arrow-in' },
        { path: '/refunds',           label: 'Refunds',           icon: 'refresh' }
      ]
    },
    {
      title: 'Purchase',
      items: [
        { path: '/vendors',          label: 'Suppliers',        icon: 'truck' },
        { path: '/bills',            label: 'Bills',            icon: 'receipt' },
        { path: '/expenses',         label: 'Expenses',         icon: 'coins' },
        { path: '/credits/vendor',   label: 'Supplier Credits', icon: 'refund' },
        { path: '/payments/paid',    label: 'Paid Dues',        icon: 'arrow-out' },
        { path: '/refund-receipts',  label: 'Refund Receipts',  icon: 'refresh' }
      ]
    },
    {
      title: 'Accounting',
      items: [
        { path: '/accounts', label: 'Chart of Accounts', icon: 'book' },
        { path: '/journal',  label: 'Journal',           icon: 'list' },
        { path: '/reports',  label: 'Reports',           icon: 'chart' },
        { path: '/vat',      label: 'VAT Return',        icon: 'receipt' },
        { path: '/audit',    label: 'Audit Log',         icon: 'list' }
      ]
    },
    {
      title: 'Admin',
      minRole: 'Admin',
      items: [
        { path: '/companies',           label: 'Companies',     icon: 'building', minRole: 'Admin' },
        { path: '/admin/users',         label: 'Users',         icon: 'users',    minRole: 'Admin' },
        { path: '/admin/security-log',  label: 'Security Log',  icon: 'list',     minRole: 'Admin' },
        { path: '/settings',            label: 'Settings',      icon: 'gear',     minRole: 'Admin' }
      ]
    }
  ];

  readonly navGroups = computed<NavGroup[]>(() => {
    const groups: NavGroup[] = [];
    for (const g of this.allNavGroups) {
      if (g.minRole && !this.auth.hasRole(g.minRole)) continue;
      const items = g.items.filter(i => !i.minRole || this.auth.hasRole(i.minRole));
      if (items.length === 0) continue;
      groups.push({ ...g, items });
    }
    return groups;
  });

  readonly user = this.auth.user;
  readonly role = this.auth.role;
  readonly isAuthenticated = this.auth.isAuthenticated;

  readonly onAuthRoute = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      startWith(null),
      map(() => {
        const url = this.router.url;
        return url.startsWith('/login') || url.startsWith('/register');
      })
    ),
    { initialValue: false }
  );

  readonly currentPageTitle = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      startWith(null),
      map(() => {
        const url = this.router.url.split('?')[0];
        for (const g of this.allNavGroups) {
          for (const i of g.items) {
            if (url.startsWith(i.path)) return i.label;
          }
        }
        return '';
      })
    ),
    { initialValue: '' }
  );

  logout(): void {
    this.auth.logout();
    this.router.navigate(['/login']);
  }
}
